import React, { useState, useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Tooltip } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { EMPLOYEE_STATUS, loadOrgEmployeesByStatus } from './actions/load-org-employees-by-status';
import { employeesForOrgIdSelector } from '../reducers/employee-selectors';

import { UiButton } from '../ui/ui-button';

import { employeeName } from './utils/employee-name';
import { sortEmployeesAlphabetically } from '../util/sort-functions';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  orgEmployees: employeesForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = {
  orgId: number;
  employeeId?: number;
  onAdd: Function;
  onRemove: Function;
  onNoSupervisor: (hasNoSupervisor: boolean) => void;
  supervisorIds: Array<number>;
  hasNoSupervisor: boolean;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

type AutocompleteOption = {
  label: string;
  value: number;
};

export const EmployeeSupervisorsBase = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    employeeId,
    orgEmployees,
    onAdd,
    onRemove,
    onNoSupervisor,
    supervisorIds,
    hasNoSupervisor,
    actions,
  }: Props) => {
    const [pickerVisible, setPickerVisible] = useState<boolean>();

    useEffect(() => {
      actions.loadOrgEmployees();
    });

    const NO_SUPERVISOR_OPTION = { label: 'No Supervisor', value: -1 };

    const handleSetNoSupervisor = (state: boolean) => () => onNoSupervisor(state);

    const handleAddSupervisor = (_e: React.ChangeEvent<{}>, option: AutocompleteOption | null) => {
      if (!option) {
        return;
      }

      if (option.value === NO_SUPERVISOR_OPTION.value) {
        onNoSupervisor(true);
      } else {
        onAdd(option.value);
      }
    };

    const handleRemoveSupervisor = (supervisorEmployeeId: number) => () => {
      onRemove(supervisorEmployeeId);
    };

    const handleOpenAutocomplete = () => {
      setPickerVisible(true);
    };

    const handleCloseAutocomplete = () => {
      setPickerVisible(false);
    };

    const supervisorIsFromIntegration = (supervisorId: number) => {
      if (!employee) {
        return false;
      }

      return (
        employee.Supervisors.find((s) => s.employeeId === supervisorId)?.EmployeeSupervisor
          .isFromIntegration || false
      );
    };

    if (!orgEmployees) {
      return null;
    }

    const employee = orgEmployees.find((e) => e.employeeId === employeeId);
    const currentOrgEmployees = orgEmployees.filter((e) => e.isCurrentEmployee);

    const unselectedSupervisors = currentOrgEmployees
      .filter((s) => !supervisorIds.includes(s.employeeId) && s.employeeId !== employeeId)
      .sort(sortEmployeesAlphabetically);
    // Show (read-only) supervisors from integrations first, then supervisors added in TE
    const selectedSupervisors = currentOrgEmployees
      .filter((s) => supervisorIds.includes(s.employeeId))
      .sort((a: Employee, b: Employee) => {
        const cmp =
          +supervisorIsFromIntegration(b.employeeId) - +supervisorIsFromIntegration(a.employeeId);
        return cmp || sortEmployeesAlphabetically(a, b);
      });

    const hasSelectedSupervisorsFromIntegration = selectedSupervisors.some((s) =>
      supervisorIsFromIntegration(s.employeeId),
    );

    // If any selected supervisors are from integration, do not allow user to remove all supervisors
    const options = (hasSelectedSupervisorsFromIntegration ? [] : [NO_SUPERVISOR_OPTION]).concat(
      unselectedSupervisors.map((s) => ({
        label: employeeName(s),
        value: s.employeeId,
      })),
    );

    return (
      <div className="u-display-flex u-alignItems-center u-flexWrap-wrap">
        {hasNoSupervisor ? (
          <Chip
            className="u-margin-top-sm u-margin-right-sm"
            size="small"
            label="No Supervisor"
            onDelete={handleSetNoSupervisor(false)}
          />
        ) : (
          selectedSupervisors.map((s) => {
            const isFromIntegration = supervisorIsFromIntegration(s.employeeId);

            return (
              <Tooltip
                title={
                  isFromIntegration ? 'Edit imported supervisors in your HR/payroll system.' : ''
                }
                key={(employee?.employeeId, s.employeeId)}
              >
                <Chip
                  className="u-margin-top-sm u-margin-right-sm"
                  key={s.employeeId}
                  size="small"
                  color="primary"
                  label={employeeName(s)}
                  onDelete={isFromIntegration ? undefined : handleRemoveSupervisor(s.employeeId)}
                />
              </Tooltip>
            );
          })
        )}
        {pickerVisible ? (
          <div className="u-width-100 u-margin-top-sm">
            <Autocomplete
              autoHighlight
              openOnFocus
              onClose={handleCloseAutocomplete}
              size="small"
              options={options}
              getOptionLabel={(option) => option.label}
              onChange={handleAddSupervisor}
              renderInput={(params) => (
                <TextField {...params} autoFocus variant="outlined" fullWidth />
              )}
            />
          </div>
        ) : (
          !hasNoSupervisor && (
            <UiButton
              className="u-margin-top-sm"
              variant="tertiary"
              size="xxs"
              onClick={handleOpenAutocomplete}
            >
              {selectedSupervisors.length === 0 ? 'Select Supervisor' : 'Add'}
            </UiButton>
          )
        )}
      </div>
    );
  },
);
